import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from '../components/AppShell.js';
import { CountrySelect } from '../components/CountrySelect.js';
import { SignupTermsLine } from '../components/SignupTermsLine.js';
import { Alert, Button, TextField } from '../components/ui.js';
import { BusinessFields } from '../components/BusinessFields.js';
import { acceptInvite, describeInvite } from '../lib/agencyApi.js';
import { ApiError } from '../lib/api.js';
import { useSession } from '../session/SessionProvider.js';

const MIN_PASSWORD_LENGTH = 10;

/**
 * Finishing an account the operator set up by hand - D-14 Item 3.4.
 *
 * A sibling of `ResetPasswordPage`, and deliberately not the same page: a reset
 * changes a password on an account that already works, and this one is the
 * account starting to exist. The two differ in what they ask for (a country),
 * what they say (the agency's name, and the signup line), and how long their
 * links live (fourteen days rather than one hour).
 *
 * **Every unusable link gets the same sentence.** Expired, replaced by a
 * resend, already used, and never issued are four different reasons with one
 * thing for the recipient to do, and telling them apart would tell a stranger
 * holding a guessed token whether it was ever real.
 */
export function SetPasswordPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const token = searchParams.get('token');

  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);

  /*
   * C-123, Craig's ruling of 30 September 2026. The invite put the agency's
   * name on the user row as a stand-in; this is the owner's own, and it is what
   * their GoHighLevel contact is called. **Starts empty, and is never prefilled
   * with the agency name**, which is exactly the stand-in it replaces.
   */
  const [ownerName, setOwnerName] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  /*
   * D-16 rulings 1 and 2, and **asked of the recipient rather than the
   * operator**. The operator who set this account up does not know the buyer's
   * website and cannot confirm business use on their behalf, so the invite
   * carries neither and this page is where both are collected.
   */
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessConfirmed, setBusinessConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setLinkError('This link is not valid. Ask us for a new one.');
      setLoading(false);
      return;
    }
    try {
      const result = await describeInvite(token);
      setAgencyName(result.agencyName);
    } catch (caught) {
      setLinkError(
        caught instanceof ApiError
          ? caught.message
          : 'We could not reach the server. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    const next: Record<string, string> = {};
    // The sign-up box's wording: the server's own, which is what sign-up shows.
    if (!ownerName.trim()) next['name'] = 'Please fill this in.';
    if (password.length < MIN_PASSWORD_LENGTH) {
      next['password'] = `Please use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    // Required, and asked here rather than on the invite form because the agency
    // knows where they are and the operator was guessing. It decides the tax on
    // their first purchase (D-13).
    if (!country) next['country'] = 'Please choose a country.';
    if (!businessWebsite.trim()) {
      next['businessWebsite'] = 'Please give a website or business profile.';
    }
    if (!businessConfirmed) {
      next['businessConfirmed'] = 'Please confirm you are buying for your business.';
    }

    if (Object.keys(next).length > 0) {
      setFieldErrors(next);
      return;
    }
    if (!token) return;

    setBusy(true);
    try {
      await acceptInvite({
        token,
        name: ownerName.trim(),
        password,
        country,
        businessWebsite: businessWebsite.trim(),
        businessConfirmed,
      });
      // The response carried the session cookies, so the shell can be told to
      // re-read who it is and the dashboard renders signed in.
      await refresh();
      void navigate('/', { replace: true });
    } catch (caught) {
      // A refusal of the name belongs under its box, as it does on sign-up.
      const nameIssue =
        caught instanceof ApiError
          ? caught.fieldIssues.find((issue) => issue.field === 'name')
          : undefined;
      if (nameIssue) setFieldErrors({ name: nameIssue.message });
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'We could not finish setting up your account. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AuthLayout title="Set your password">
        <p className="text-sm text-fg-muted" role="status">
          Loading…
        </p>
      </AuthLayout>
    );
  }

  if (linkError !== null) {
    return (
      <AuthLayout
        title="Set your password"
        footer={
          <Link to="/sign-in" className="font-medium text-fg underline">
            Back to sign in
          </Link>
        }
      >
        <Alert>{linkError}</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set your password"
      // `exactOptionalPropertyTypes` is on: an absent subtitle is the property
      // not being there, not the property being undefined.
      {...(agencyName ? { subtitle: `Finish setting up ${agencyName}.` } : {})}
      footer={
        <Link to="/sign-in" className="font-medium text-fg underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-5" noValidate>
        {error ? <Alert>{error}</Alert> : null}

        <TextField
          label="Your name"
          name="yourName"
          autoComplete="name"
          required
          value={ownerName}
          onChange={(event) => setOwnerName(event.target.value)}
          error={fieldErrors['name']}
        />

        <TextField
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors['password']}
        />

        <CountrySelect
          label="Country"
          // WAIT-A item 6. The tax sentence was true and unhelpful: prices are
          // ex-VAT (D-13) and whatever applies is added by Stripe at checkout,
          // so telling somebody setting a password that this decides their tax
          // invites a question nobody on this page can answer.
          hint="Where your business is based."
          value={country}
          onChange={setCountry}
          error={fieldErrors['country']}
        />

        <BusinessFields
          website={businessWebsite}
          onWebsiteChange={setBusinessWebsite}
          confirmed={businessConfirmed}
          onConfirmedChange={setBusinessConfirmed}
          websiteError={fieldErrors['businessWebsite']}
          confirmedError={fieldErrors['businessConfirmed']}
        />

        <Button type="submit" busy={busy} className="w-full">
          Set password and sign in
        </Button>

        {/* Ruling 9. A statement, not a checkbox; see SignupTermsLine. */}
        <SignupTermsLine />
      </form>
    </AuthLayout>
  );
}
